import type { Direction, GameState, TileType } from "../domain/types";

const codes: Partial<Record<TileType, string>> = {
  coin: "C",
  treasure: "T",
  gem: "G",
  relic: "R",
  trap: "!",
  poison: "P",
  portal: "O",
  freeze: "F",
  multiplier: "x2",
  collapse: "#",
  exit: "E",
  curse: "X",
  relicFragment: "RF",
  secretClue: "?",
  mystery: "?",
};

const icons: Partial<Record<TileType, string>> = {
  coin: '<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="3.2"/>',
  treasure: '<rect x="4" y="11" width="16" height="8" rx="1.5"/><path d="M4 11c0-4 3.5-6 8-6s8 2 8 6"/><path d="M4 14.5h16"/>',
  gem: '<path d="M12 3l6 6-6 12-6-12z"/><path d="M6 9h12"/>',
  relic: '<path d="M12 3l2.4 5.8L21 9.3l-4.8 4.1L17.5 20 12 16.6 6.5 20l1.3-6.6L3 9.3l6.6-.5z"/>',
  relicFragment: '<path d="M12 3l3.5 9-3.5 9-3.5-9z"/>',
  trap: '<path d="M12 3l9 16H3z"/><path d="M12 10v4"/><circle cx="12" cy="17.1" r="0.9" fill="currentColor" stroke="none"/>',
  poison: '<path d="M12 3c4 5 6 8.5 6 11.5A6 6 0 1 1 6 14.5C6 11.5 8 8 12 3z"/>',
  curse: '<circle cx="12" cy="12" r="8"/><path d="M9 9l6 6M15 9l-6 6"/>',
  portal: '<ellipse cx="12" cy="12" rx="8" ry="5"/><ellipse cx="12" cy="12" rx="3.6" ry="2.2"/>',
  freeze: '<path d="M12 3v18M4.8 7.5l14.4 9M19.2 7.5L4.8 16.5"/>',
  multiplier: '<path d="M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2z"/>',
  collapse: '<rect x="4" y="4" width="16" height="16" rx="1.5"/><path d="M8 4l3 6-4 3 5 7"/>',
  exit: '<path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4"/><path d="M12 12h9M17 8l4 4-4 4"/>',
  secretClue: '<circle cx="12" cy="9" r="3.5"/><path d="M12 12.5l-2.4 8h4.8z"/>',
  mystery: '<circle cx="12" cy="9" r="3.5"/><path d="M12 12.5l-2.4 8h4.8z"/>',
};

const playerIcon = '<path d="M12 2 3 21h18z"/><circle cx="12" cy="14.5" r="2.2" fill="currentColor" stroke="none"/>';

export class GridRenderer {
  private previousDiscovery: string[] = [];
  private previousFeedbackId?: number;
  private previousDamageFeedbackId?: number;

  constructor(
    private readonly parent: HTMLElement,
    private readonly onMove: (direction: Direction) => void,
    private readonly onInspect: (direction: Direction) => void,
  ) {}

  render(
    state: GameState,
    tutorialTarget: number | undefined,
    reducedMotion: boolean,
    _highContrast: boolean,
    feedbackId?: number,
    damageFeedbackId?: number,
  ): void {
    const priorDiscovery = this.previousDiscovery;
    this.parent.style.setProperty("--grid-size", String(state.size));
    this.parent.classList.toggle("reduced-grid-motion", reducedMotion);
    this.parent.replaceChildren(...state.grid.map((tile, index) => {
      const visible = tile.discoveryState !== "unknown" || index === state.playerIndex;
      const isPlayer = index === state.playerIndex;
      const cell = document.createElement("button");
      cell.type = "button";
      cell.className = `grid-tile tile-${tile.type} visibility-${tile.discoveryState}`;
      cell.style.setProperty("--tile-seed", String((index * 47) % 11));
      cell.setAttribute("aria-label", isPlayer ? "Your position" : visible ? `${tile.type} tile` : "Unknown tile");
      if (index === tutorialTarget) cell.classList.add("tutorial-target");
      if (isPlayer) cell.classList.add("player-tile");
      if (!reducedMotion && priorDiscovery[index] === "unknown" && tile.discoveryState !== "unknown") {
        cell.classList.add("tile-revealing");
      }
      if (isPlayer) {
        cell.innerHTML = `<svg class="tile-icon player-icon" viewBox="0 0 24 24" aria-hidden="true">${playerIcon}</svg>`;
      } else if (visible) {
        const icon = icons[tile.type];
        cell.innerHTML = icon
          ? `<svg class="tile-icon" viewBox="0 0 24 24" aria-hidden="true">${icon}</svg><i class="tile-code">${codes[tile.type] ?? ""}</i>`
          : "";
      } else {
        cell.innerHTML = '<span class="tile-glyph tile-unknown" aria-hidden="true">?</span>';
      }
      if (!visible && this.isAdjacent(index, state.playerIndex, state.size) && tile.omen !== "none") {
        const warning = document.createElement("i");
        warning.className = `tile-warning omen-${tile.omen}`;
        warning.textContent = tile.omen === "cursed_chamber" ? "X" : tile.omen === "possible_relic" ? "R" : "!";
        cell.append(warning);
      }
      cell.onclick = () => this.requestMove(index, state);
      cell.onmouseenter = () => this.inspect(index, state);
      return cell;
    }));
    this.previousDiscovery = state.grid.map(tile => tile.discoveryState);
    if (feedbackId !== undefined && feedbackId !== this.previousFeedbackId) {
      this.previousFeedbackId = feedbackId;
      this.showFeedback(state, reducedMotion);
    }
    if (damageFeedbackId !== undefined && damageFeedbackId !== this.previousDamageFeedbackId) {
      this.previousDamageFeedbackId = damageFeedbackId;
      this.showDamageFeedback(state, reducedMotion);
    }
  }

  setPaused(paused: boolean): void {
    this.parent.classList.toggle("grid-paused", paused);
  }

  destroy(): void {
    this.parent.replaceChildren();
  }

  private requestMove(index: number, state: GameState): void {
    const direction = this.directionTo(index, state);
    if (direction) this.onMove(direction);
  }

  private inspect(index: number, state: GameState): void {
    const direction = this.directionTo(index, state);
    if (direction) this.onInspect(direction);
  }

  private directionTo(index: number, state: GameState): Direction | undefined {
    const current = state.playerIndex;
    if (index === current - state.size) return "up";
    if (index === current + state.size) return "down";
    if (index === current - 1 && Math.floor(index / state.size) === Math.floor(current / state.size)) return "left";
    if (index === current + 1 && Math.floor(index / state.size) === Math.floor(current / state.size)) return "right";
    return undefined;
  }

  private isAdjacent(a: number, b: number, size: number): boolean {
    return Math.abs(Math.floor(a / size) - Math.floor(b / size)) + Math.abs(a % size - b % size) === 1;
  }

  private showFeedback(state: GameState, reducedMotion: boolean): void {
    const feedback = state.lastTreasureFeedback;
    if (!feedback) return;
    const tile = this.parent.children.item(feedback.tileIndex) as HTMLElement | null;
    if (!tile) return;
    const text = document.createElement("span");
    text.className = "floating-reward";
    text.textContent = `${feedback.gold ? `+${feedback.gold} Gold` : `+${feedback.score} Score`} - Combo x${feedback.combo}`;
    tile.append(text);
    if (!reducedMotion) {
      for (let index = 0; index < 8; index++) {
        const particle = document.createElement("i");
        particle.className = "gold-particle";
        particle.style.setProperty("--angle", `${index * 45}deg`);
        tile.append(particle);
        window.setTimeout(() => particle.remove(), 700);
      }
    }
    window.setTimeout(() => text.remove(), reducedMotion ? 500 : 950);
  }

  private showDamageFeedback(state: GameState, reducedMotion: boolean): void {
    const feedback = state.lastDamageFeedback;
    if (!feedback) return;
    const tile = this.parent.children.item(feedback.tileIndex) as HTMLElement | null;
    if (!tile) return;
    const text = document.createElement("span");
    text.className = "floating-damage";
    text.textContent = `-${feedback.amount} HP`;
    tile.append(text);
    tile.classList.add("tile-hit");
    window.setTimeout(() => tile.classList.remove("tile-hit"), reducedMotion ? 10 : 320);
    if (!reducedMotion) {
      for (let index = 0; index < 6; index++) {
        const particle = document.createElement("i");
        particle.className = "trap-particle";
        particle.style.setProperty("--angle", `${index * 60}deg`);
        tile.append(particle);
        window.setTimeout(() => particle.remove(), 600);
      }
    }
    window.setTimeout(() => text.remove(), reducedMotion ? 500 : 850);
  }
}
